/**
 * Responsive image URLs. Cloudinary URLs get `f_auto,q_auto,w_<n>` transforms (+ srcset);
 * anything else is returned unchanged.
 */
const CLOUDINARY_UPLOAD = /^(https:\/\/res\.cloudinary\.com\/[^/]+\/image\/(?:upload|private|authenticated))\/(.*)$/;
/** A transformation segment: comma-separated Cloudinary parameters (e.g. `c_fill,w_200` or `e_blur:300`). */
const PARAM = /^(?:w|h|c|q|f|e|g|ar|dpr|t|fl|x|y|z|r|a|b|co|bo|l|o|so|du|eo|vc|ac)_[^,/]+$/;
const isTransform = (segment: string) => !segment.includes(".") && segment.split(",").every((part) => PARAM.test(part));

export function isCloudinary(url: string | null | undefined): boolean {
  return !!url && CLOUDINARY_UPLOAD.test(url);
}

/**
 * True when the URL can render on our https pages: absolute https (or same-origin path / data / blob)
 * with no "undefined" / "null" holes from a missing env var.
 */
export function isSafeImageUrl(url: string | null | undefined): url is string {
  if (!url || typeof url !== "string") return false;
  if (/(^|\/)(undefined|null)(\/|$)/.test(url)) return false;
  if (url.startsWith("data:image/") || url.startsWith("blob:")) return true;
  if (url.startsWith("/") && !url.startsWith("//")) return true;
  if (url.startsWith("https://")) return true;
  // http:// images are blocked as mixed content on https pages; allowed only when the page is http (local dev).
  return url.startsWith("http://") && typeof window !== "undefined" && window.location.protocol === "http:";
}

/** Strip transformation segments a stored URL may already carry, keeping the version and public id. */
function splitCloudinary(url: string): { base: string; rest: string } | null {
  const match = CLOUDINARY_UPLOAD.exec(url);
  if (!match) return null;
  const segments = match[2]!.split("/");
  while (segments.length > 1 && isTransform(segments[0]!)) segments.shift();
  return { base: match[1]!, rest: segments.join("/") };
}

/** Insert a transformation right after `/upload/` (replacing any existing one). */
export function cloudinaryUrl(url: string, transform: string): string {
  const parts = splitCloudinary(url);
  if (!parts) return url;
  return `${parts.base}/${transform}/${parts.rest}`;
}

export function imageUrl(url: string | null | undefined, width?: number, opts: { crop?: "fill" | "limit"; height?: number } = {}): string {
  if (!url) return "";
  if (!isCloudinary(url) || !width) return url;
  const parts = ["f_auto", "q_auto", `w_${width}`, opts.height ? `h_${opts.height}` : "", `c_${opts.crop ?? "limit"}`]
    .filter(Boolean)
    .join(",");
  return cloudinaryUrl(url, parts);
}

/** Tiny blurred preview shown while the real image loads (Cloudinary only). */
export function blurUrl(url: string | null | undefined): string {
  if (!url || !isCloudinary(url)) return "";
  return cloudinaryUrl(url, "e_blur:1000,q_1,w_40,f_auto");
}

const WIDTHS = [240, 360, 480, 640, 800, 1080, 1440, 1920];

export function srcSet(url: string | null | undefined, maxWidth = 1080): string | undefined {
  if (!url || !isCloudinary(url)) return undefined;
  return WIDTHS.filter((w) => w <= maxWidth)
    .map((w) => `${imageUrl(url, w)} ${w}w`)
    .join(", ");
}
